import { FC, /* MouseEvent, */ useState } from 'react';
// TODO(AI-1338): usado só pelo botão de editar, desligado no lançamento — descomentar no futuro.
// import { BvIcon } from 'bevi-icon';
import { InssMarginFinancial, InssMarginItem } from '../../../models/InssMargin/InssMarginModels';
import { useInssMargin } from '../../../providers/InssMargin/InssMarginProvider';
import { getFinanceiraLogo } from '../../../utils/FinanceiraLogoMap';
import { formatCpf, formatIsoDate, formatPhone, onlyDigits } from '../../../utils/InssMarginFormat';
import logoC6Compact from '../../../assets/img/c6Mobile.svg';
import logoPresenca from '../../../assets/img/presença_bank_horizontal.svg';
import logoDaycoval from '../../../assets/img/daycoval_horizontal.svg';
import logoBmg from '../../../assets/img/bmg_horizontal.svg';
import logoFacta from '../../../assets/img/factaMobile.svg';
import logoTotalCash from '../../../assets/img/totalcash_horizontal.svg';
import logoVctex from '../../../assets/img/vctex_horizontal.svg';
import logoBbMais from '../../../assets/img/bb-mais.svg';
import logoBanrisul from '../../../assets/img/banrisul.svg';
import logoBrb from '../../../assets/img/banco-brb.svg';
import logoDigio from '../../../assets/img/digio.svg';
import logoFinanto from '../../../assets/img/finanto.svg';
import logoMercantil from '../../../assets/img/banco-mercantil.svg';
import logoQueroMais from '../../../assets/img/quero-mais-credito.svg';
import logoCaixa from '../../../assets/img/caixa.svg';
import logoNbc from '../../../assets/img/nbc-bank.svg';
import logoParana from '../../../assets/img/parana-banco.svg';
import logoSafra from '../../../assets/img/banco-safra.svg';
// TODO(AI-1338): arquivar desligado no lançamento — descomentar no futuro.
// import InssMarginDiscardPopover from './InssMarginDiscardPopover';

// A carteira identifica as financeiras pelo nome do cadastro do Lacqua (GET /financeiras).
// Só o consignado do C6 ganha logo: as demais "C6 ..." ficariam com logos iguais, então
// vão pelo nome. O c6bank.svg padrão tem muito respiro no viewBox e fica minúsculo no
// chip; aqui usamos a versão recortada (a mesma do mobile). Nome fora do mapa vai em texto.
const INSS_MARGIN_LOGOS: Record<string, string> = {
    'banco c6 consig': logoC6Compact,
    'icred': getFinanceiraLogo('ICRED'),
    'bmg': logoBmg,
    'banco daycoval': logoDaycoval,
    'facta financeira': logoFacta,
    'presenca bank': logoPresenca,
    'total cash': logoTotalCash,
    'vctex': logoVctex,
    'bb mais': logoBbMais,
    'banrisul': logoBanrisul,
    'brb': logoBrb,
    'digio': logoDigio,
    'finanto': logoFinanto,
    'banco mercantil': logoMercantil,
    'quero + legado': logoQueroMais,
    'banco caixa economica federal': logoCaixa,
    'nbc bank': logoNbc,
    'paraná banco': logoParana,
    'banco safra': logoSafra,
};

const getInssMarginLogo = ({ name }: InssMarginFinancial) => INSS_MARGIN_LOGOS[name.trim().toLowerCase()] ?? '';

interface InssMarginRowProps {
    item: InssMarginItem;
    // TODO(AI-1338): editar desligado no lançamento — descomentar no futuro.
    // onOpenRecord: (item: InssMarginItem, opener: HTMLButtonElement) => void;
}

// A base pode trazer o celular com DDI (55) ou zero de operadora na frente; aqui sobra
// só DDD + número (10/11 dígitos), ou null se não der para montar um celular.
const normalizePhone = (phone: string) => {
    let digits = onlyDigits(phone).replace(/^0+/, '');
    if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) digits = digits.slice(2);
    return digits.length === 10 || digits.length === 11 ? digits : null;
};

const PhoneCell: FC<{ phone: string | null }> = ({ phone }) => {
    if (!phone) {
        return <td className="inss-margin__nowrap"><span className="inss-margin__muted">Não informado</span></td>;
    }
    const digits = normalizePhone(phone);
    return (
        <td className="inss-margin__nowrap">
            {digits ? (
                <a
                    className="inss-margin__whatsapp"
                    href={`https://wa.me/55${digits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir conversa no WhatsApp"
                >
                    {formatPhone(digits)}
                </a>
            ) : formatPhone(phone)}
        </td>
    );
};

// TODO(AI-1338): editar e arquivar desligados no lançamento — descomentar `onOpenRecord`,
// `discard`, `isValidated` e a coluna de ações abaixo no futuro.
export const InssMarginOpportunityRow: FC<InssMarginRowProps> = ({ item /* , onOpenRecord */ }) => {
    const { financials, pendingFinancialIds, toggleFinancial /* , discard */ } = useInssMargin();
    const isSavingFinancials = pendingFinancialIds.includes(item.id);
    // const isValidated = !!item.validated_at;
    const lastProposal = formatIsoDate(item.last_proposal_at);

    return (
        <tr>
            <td>
                <div className="inss-margin__name">{item.name}</div>
                <div className="inss-margin__sub">{formatCpf(item.cpf)}</div>
                {lastProposal && (
                    <div className="inss-margin__sub inss-margin__nowrap">Última proposta {lastProposal}</div>
                )}
                {/* Vai chamar o "qualificar" quando o endpoint existir. */}
                <button
                    type="button"
                    className="inss-margin__btn inss-margin__btn--secondary inss-margin__sanitize-btn"
                    disabled
                    title="Disponível em breve"
                >
                    Higienizar
                </button>
            </td>
            <PhoneCell phone={item.phone} />
            <td>
                <div className="inss-margin__banks">
                    {financials.map((financial) => {
                        const checked = item.financials.includes(financial.id);
                        const logo = getInssMarginLogo(financial);
                        return (
                            <button
                                key={financial.id}
                                type="button"
                                role="checkbox"
                                aria-checked={checked}
                                aria-label={financial.name}
                                title={financial.name}
                                className={`inss-margin__bank${checked ? ' is-checked' : ''}`}
                                data-financial={financial.name.trim().toLowerCase()}
                                disabled={isSavingFinancials}
                                onClick={() => toggleFinancial(item, financial.id)}
                            >
                                {logo ? <img src={logo} alt="" /> : <span>{financial.name}</span>}
                            </button>
                        );
                    })}
                </div>
            </td>
            {/* TODO(AI-1338): editar e arquivar desligados no lançamento — descomentar no futuro. */}
            {/* <td className="inss-margin__actions">
                <button
                    type="button"
                    className={`inss-margin__icon-btn inss-margin__edit-btn${isValidated ? ' is-ok' : ''}`}
                    title={isValidated ? 'Dados validados — ver e editar dados do cliente' : 'Ver, validar e editar dados do cliente'}
                    aria-label={`Ver e editar dados de ${item.name}${isValidated ? ' (validado)' : ''}`}
                    onClick={(e: MouseEvent<HTMLButtonElement>) => onOpenRecord(item, e.currentTarget)}
                >
                    <BvIcon name="editSquare" size={1.1} variant="solid" />
                </button>
                <InssMarginDiscardPopover clientName={item.name} onConfirm={(reason, note) => discard(item, reason, note)} />
            </td> */}
        </tr>
    );
};

export const InssMarginDiscardedRow: FC<{ item: InssMarginItem }> = ({ item }) => {
    const { restore } = useInssMargin();
    const [isRestoring, setIsRestoring] = useState(false);

    const handleRestore = async () => {
        setIsRestoring(true);
        const ok = await restore(item);
        // Com sucesso a linha sai da lista e o componente desmonta.
        if (!ok) setIsRestoring(false);
    };

    return (
        <tr>
            <td>
                <div className="inss-margin__name">{item.name}</div>
                <div className="inss-margin__sub">{formatCpf(item.cpf)}</div>
            </td>
            <PhoneCell phone={item.phone} />
            <td>{item.discarded_reason}</td>
            <td className="inss-margin__nowrap inss-margin__muted">{formatIsoDate(item.discarded_at)}</td>
            <td className="inss-margin__actions">
                <button
                    type="button"
                    className="inss-margin__btn inss-margin__btn--secondary"
                    onClick={handleRestore}
                    disabled={isRestoring}
                >
                    {isRestoring ? 'Trazendo…' : 'Trazer de volta'}
                </button>
            </td>
        </tr>
    );
};
